import type {
  GetApiV1ThreeLetterCodePartnerAdminsClients200One,
  GetApiV1ThreeLetterCodePartnerAdminsCustomFieldDefinitions200,
  GetApiV1ThreeLetterCodePartnerAdminsPartnerBranches200,
  GetApiV1ThreeLetterCodePartnerAdminsPartnerFeatureFlags200,
} from '@fukuri-admins/apis/client/gen/model';
import { formatDate } from '@fukuri-admins/utils/common/date/formatDate';
import { getContractPlanLink } from '~/utils/getLinks';
import type { ClientListPresentation } from '../../presentations';
import { buildCustomFieldColumns } from '../buildCustomFieldColumns';
import { extractCustomFields } from '../extractCustomFields';
import { formatCustomFieldValues } from '../formatCustomFieldValues';

type PresentationProps = React.ComponentProps<typeof ClientListPresentation>;

type Args = {
  /** API から取得した利用企業一覧データ */
  clientsData: GetApiV1ThreeLetterCodePartnerAdminsClients200One;
  /**
   * 検索条件が適用されているか
   *
   * true のとき 0 件でも Empty 画面を表示しない（PEN-309 対応）
   */
  isFiltered: boolean;
  /** パートナー機能フラグ API レスポンス（raw） */
  featureFlagsData: GetApiV1ThreeLetterCodePartnerAdminsPartnerFeatureFlags200;
  /**
   * カスタムフィールド定義 API レスポンス（raw）
   *
   * Suspense で取得するため `custom_field_definitions` は必ず存在する（required フィールド）
   */
  customFieldDefinitionsData: GetApiV1ThreeLetterCodePartnerAdminsCustomFieldDefinitions200;
  /**
   * 未与信件数（useGetHasUngrantedCredit の totalCount から導出済み）
   *
   * creditEvaluationEnabled=false のとき 0。
   */
  ungrantedCreditCount: number;
  /**
   * マスタ取引店舗一覧 API レスポンス（raw）（PEN-985）
   *
   * `partner_branches.length > 0`（hasPartnerBranch）の導出に使う。
   */
  partnerBranchesData: GetApiV1ThreeLetterCodePartnerAdminsPartnerBranches200;
};

type IConvertToPresentationProps = (params: Args) => {
  rows: PresentationProps['rows'];
  customFieldColumns: PresentationProps['customFieldColumns'];
  /** 利用企業が 0 件かどうか */
  isEmpty: boolean;
  /** 与信評価機能フラグ（partner_feature_flags から派生） */
  creditEvaluationEnabled: boolean;
  /** 未与信件数（hook 内で解釈済み。creditEvaluationEnabled=false のとき 0） */
  ungrantedCreditCount: number;
  /**
   * マスタ取引店舗一覧に1件以上の取引店舗が存在するか（PEN-985）
   *
   * ClientsTable の取引店舗列・DeleteClientDialog の取引店舗表示の両方の
   * 表示可否判定として Container 側で使う。
   */
  hasPartnerBranch: boolean;
};

/**
 * API 型（snake_case）を ClientList Presentation の props 型（camelCase）に変換する。
 *
 * - 出力型は `ClientListPresentation` の Props から導出し、型乖離を防ぐ
 * - isEmpty フラグを導出して Container 層での表示出し分けに使用する
 * - カスタムフィールド定義に基づいてカスタムフィールド列定義（customFieldColumns）を動的に構築する
 * - カスタムフィールド値は `custom_field_${label}` キーで rows に展開される
 * - カスタムフィールド定義は target_model === 'client' のものだけに絞り込んでから列定義・値のフォーマットに使う
 *   （target_model === 'user_profile' の定義は企業に値が存在しないため常に空欄列になるので除外する。PEN-920）
 * - カスタムフィールド「取引店舗」は無条件に除外する（固定列 PartnerBranch との二重表示防止。PEN-988 / PEN-989）
 * - 契約プラン: client.contract_plan が null/undefined のとき contractPlanName は '-'、
 *   contractPlanLink は空文字（Presentation 側でリンク非表示の判定に使う）
 * - 登録案内カード: client.wants_activation_card をそのまま表示意図（showActivationCardBadge）として渡す。
 *   希望なしの行は空セルにするため '-' へのフォールバックは持たない
 * - 固定列の組み立ては Presentation 層（ClientsTable）が担う
 * - creditEvaluationEnabled=true のとき rows に isCreditGranted を含め、与信状況列を先頭に追加する
 * - ungrantedCreditCount は hook 内で解釈済みの値をそのまま受け取る
 * - hasPartnerBranch: partnerBranchesData.partner_branches.length > 0 として導出する（PEN-985）。
 *   Container 側で取引店舗列の表示可否判定にそのまま使う
 * - ページネーション（pager）の組み立ては Container 層（generatePagerProps）が担う
 */
export const convertToPresentationProps: IConvertToPresentationProps = ({
  clientsData,
  isFiltered,
  featureFlagsData,
  customFieldDefinitionsData,
  ungrantedCreditCount,
  partnerBranchesData,
}) => {
  const clients = clientsData.clients ?? [];

  // Suspense で取得するため custom_field_definitions は必ず存在する（required フィールド）
  // 企業（Client）一覧では target_model === 'client' の定義のみを表示対象にする（PEN-920）
  // カスタムフィールド「取引店舗」は無条件に除外する（固定列 PartnerBranch との二重表示防止。PEN-988 / PEN-989）
  const customFieldDefinitions = customFieldDefinitionsData.custom_field_definitions.filter(
    def => def.target_model === 'client' && def.label !== '取引店舗'
  );

  // 与信評価機能フラグを partner_feature_flags 配列から派生する
  const creditEvaluationEnabled =
    featureFlagsData.partner_feature_flags.find(f => f.feature_key === 'credit_evaluation')
      ?.enabled ?? false;

  const rows: PresentationProps['rows'] = clients.map(client => {
    const baseRow = {
      id: client.id,
      name: client.name,
      // グループ: groups 配列の最初のグループ名を表示。未設定の場合は '-'
      groupName: client.groups?.[0]?.name ?? '-',
      // 取引店舗: partner_branch?.name を使用。未設定の場合は '-'
      partnerBranchName: client.partner_branch?.name ?? '-',
      // 担当者名: 未設定の場合は '-'
      contactPersonName: client.contact_person_name ?? '-',
      // 申込人数: employee_count が null/undefined の場合は '-'（MMT-219）
      employeeCount: client.employee_count != null ? String(client.employee_count) : '-',
      // 利用人数（表示用）: user_count が null/undefined の場合は '-'（MMT-219）
      userCountDisplay: client.user_count != null ? String(client.user_count) : '-',
      // 課金対象人数: non_invited_user_count が null/undefined の場合は '-'（MMT-219）
      nonInvitedUserCount:
        client.non_invited_user_count != null ? String(client.non_invited_user_count) : '-',
      // 契約プラン: contract_plan は nullable（有効な契約がない場合 null）。
      // リンク表示可否は Presentation 側で contractPlanLink の有無から判定する（LEA-3497）
      contractPlanName: client.contract_plan?.name ?? '-',
      contractPlanLink: client.contract_plan ? getContractPlanLink(client.contract_plan.id) : '',
      // 登録案内カード: 送付希望の行だけバッジを表示する。希望なしは空セルにするため '-' へのフォールバックは持たない
      showActivationCardBadge: client.wants_activation_card,
      // 利用者数: user_count は openapi 上 optional のため fallback 0 を適用（削除ダイアログの表示情報として使用）
      userCount: client.user_count ?? 0,
      createdAt:
        client.created_at != null
          ? formatDate({ date: new Date(client.created_at * 1000), formatType: 'yyyy/MM/dd H:mm' })
          : '-',
      updatedAt:
        client.updated_at != null
          ? formatDate({ date: new Date(client.updated_at * 1000), formatType: 'yyyy/MM/dd H:mm' })
          : '-',
      // 与信状態: creditEvaluationEnabled=true のときのみ含める
      ...(creditEvaluationEnabled && {
        isCreditGranted: client.is_credit_granted,
      }),
    };

    if (customFieldDefinitions.length === 0) {
      return baseRow;
    }

    // カスタムフィールド値を展開してフォーマットする
    const extracted = extractCustomFields(client.custom_field_values ?? []);
    const formatted = formatCustomFieldValues(extracted, customFieldDefinitions);

    return { ...baseRow, ...formatted };
  });

  // カスタムフィールド列定義を構築する（固定列の組み立ては Presentation 層が担う）
  const customFieldColumns = buildCustomFieldColumns(customFieldDefinitions);

  // 検索条件あり × 0 件は Empty 画面を表示しない（PEN-309 対応）
  const isEmpty = clients.length === 0 && !isFiltered;

  // マスタ取引店舗一覧に1件以上存在するか（PEN-985）
  const hasPartnerBranch = partnerBranchesData.partner_branches.length > 0;

  return {
    rows,
    customFieldColumns,
    isEmpty,
    creditEvaluationEnabled,
    ungrantedCreditCount,
    hasPartnerBranch,
  };
};
